import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Theme, Team } from '@yandex-int/k-common/typings';
// @ts-ignore
import { Route, Switch } from 'platform-components/src/components/router/router';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { getSelectedUmk } from 'common.components/umk/selectors';
import { getCurrentClass } from 'common.components/user/selectors';
import * as themesActions from 'store/themes/actions';
import * as themesSelectors from 'store/themes/selectors';
import { scrollTo } from 'utils/scroll-to';

import { ReduxTeacherStore } from '../../../typings';
import SectionPageActual from '../../library.section-page/_actual/section-page_actual';

import PageContent from './__content/library__pages-content';

interface StoreProps {
    themes: Array<Theme>;
    actualThemes: Array<Theme>;
    loadingStatus: LOADING_STATUSES;
    isActualTheme: boolean;

    courseId: number | null;
    currentTeam: Team | null;
    shouldLoadActualThemes: boolean;
    umk: string | null;
}

interface DispatchProps {
    getThemesTreeRequest: typeof themesActions.getThemesTreeRequest;
    getThemesTreeSuccess: typeof themesActions.getThemesTreeSuccess;
    loadActualThemesRequest: typeof themesActions.getActualThemesRequest;
}

interface HOCProps extends WithRouterPathProps {}

interface Props extends StoreProps, DispatchProps, HOCProps {}

interface Context {
    data: {
        getThemesFlatFullTree?: Array<Theme>;
    };
    initialRender: boolean;
    staticUrls: {
        externalCreateLesson: string;
        labLibraryActualThemes: string;
    };
}

export class LibraryPages extends React.Component<Props, Context> {
    static contextTypes = {
        data: PropTypes.object,
        initialRender: PropTypes.bool.isRequired,
        staticUrls: PropTypes.object.isRequired,
    };

    // eslint-disable-next-line complexity
    constructor(props: Props, context: Context) {
        super(props, context);

        const {
            isActualTheme,
            location: { pathname },
            shouldLoadActualThemes,
            match: {
                params: { subject, themeId },
            },
            loadingStatus,
            getThemesTreeRequest,
            getThemesTreeSuccess,
        } = props;
        const { data, initialRender, staticUrls } = context;

        if (initialRender && data.getThemesFlatFullTree) {
            const themes: Array<Theme> = data.getThemesFlatFullTree;
            const rootTheme = themes.find((theme) => !theme.parent);

            if (rootTheme) {
                getThemesTreeSuccess(subject, Number(themeId), themes, rootTheme.id);
            }
        } else if (themeId && loadingStatus === LOADING_STATUSES.UNSENT) {
            getThemesTreeRequest(subject, Number(themeId));
        }

        const isActualThemePage =
            isActualTheme || matchPath(pathname, { path: staticUrls.labLibraryActualThemes, exact: true });

        if (isActualThemePage && shouldLoadActualThemes) {
            this.loadActualThemes();
        }

        this.scrollTop();
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps: Props) {
        const {
            match: {
                params: { subject: prevSubject, themeId: prevThemeId },
            },
            currentTeam: prevCurrentTeam,
        } = prevProps;
        const {
            match: {
                params: { themeId, subject },
            },
            loadingStatus,
            getThemesTreeRequest,
            isActualTheme,
            location: { pathname },
            shouldLoadActualThemes,
            currentTeam,
        } = this.props;

        const themesChanged = prevThemeId !== themeId;

        if (themesChanged) {
            this.scrollTop();

            if (themeId && loadingStatus === LOADING_STATUSES.UNSENT) {
                getThemesTreeRequest(subject, Number(themeId));
            }
        }

        const subjectChanged = subject !== prevSubject;
        const teamChanged = currentTeam !== prevCurrentTeam;
        const { staticUrls } = this.context;

        const isActualThemePage =
            isActualTheme || matchPath(pathname, { path: staticUrls.labLibraryActualThemes, exact: true });

        if ((subjectChanged || teamChanged) && isActualThemePage && shouldLoadActualThemes) {
            this.loadActualThemes();
        }
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    loadActualThemes = () => {
        const {
            currentTeam,
            umk,
            courseId,
            loadActualThemesRequest,
            match: {
                params: { subject },
            },
        } = this.props;

        if (courseId && currentTeam) {
            const { level } = currentTeam;
            loadActualThemesRequest({ courseId, grade: level, umk, subject });
        }
    };

    scrollTop = () => {
        scrollTo(0, 0);
    };

    doesThemeExist = () => {
        const {
            match: { params },
            themes,
            actualThemes,
            isActualTheme,
        } = this.props;
        const themeId = Number(params.themeId);

        if (isActualTheme) {
            return actualThemes && actualThemes.some(({ id }) => id === themeId);
        }

        return themes && themes.some(({ id }) => id === themeId);
    };

    render = () => {
        const { staticUrls } = this.context;

        return (
            <Switch>
                <Route component={SectionPageActual} exact path={staticUrls.labLibraryActualThemes} />
                <Route component={PageContent} />
            </Switch>
        );
    };
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: HOCProps): StoreProps => {
    const {
        location: { search },
        match: {
            params: { subject, themeId: thId, classId },
        },
    } = ownProps;
    const query = qs.parse(search);
    const themeId = Number(thId);
    const currentTeam = getCurrentClass(storeState, { classId });
    const grade = currentTeam?.level || Number(query.grade);
    const courseId = getCurrentYearCourseId(storeState);

    return {
        themes: themesSelectors.getThemes(storeState, { subject, grade }),
        actualThemes: themesSelectors.getActualThemes(storeState, { subject, courseId }),
        loadingStatus: themesSelectors.getThemesTreeLoadingStatus(storeState, { subject, themeId, grade }),
        isActualTheme: themesSelectors.getIsActualTheme(storeState, { themeId }),
        shouldLoadActualThemes: themesSelectors.getShouldLoadActualThemes(storeState, { classId, subject, courseId }),
        umk: getSelectedUmk(storeState, { classId, subject }),
        currentTeam,
        courseId,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            getThemesTreeRequest: themesActions.getThemesTreeRequest,
            getThemesTreeSuccess: themesActions.getThemesTreeSuccess,
            loadActualThemesRequest: themesActions.getActualThemesRequest,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(LibraryPages));
